<template>
  <b-modal
    ref="create-confirm-modal"
    hide-footer
    hide-header
    id="upload-image-modal"
    no-close-on-backdrop
  >
    <div
      class="modal__title--tab"
    >
      <a
        v-if="tabs.includes('upload')"
        @click="handleTabChange('upload')"
        :class="{ active: selectedTab === 'upload' }"
      >
        {{ $i18nPresenter.t('Upload') }}
      </a>
      <a
        v-if="tabs.includes('unsplash')"
        @click="handleTabChange('unsplash')"
        :class="{ active: selectedTab === 'unsplash' }"
      >
        <span class="d-none d-xl-block d-lg-block d-md-block">{{ $i18nPresenter.t('Photo library') }}</span>
        <span class="d-block d-md-none">{{ $i18nPresenter.t('Photos') }}</span>
      </a>
      <a
        v-if="tabs.includes('giphy')"
        @click="handleTabChange('giphy')"
        :class="{ active: selectedTab === 'giphy' }"
      >
        <span class="d-none d-xl-block d-lg-block d-md-block">{{ $i18nPresenter.t('Animated GIF library') }}</span>
        <span class="d-block d-md-none">GIFs</span>
      </a>
    </div>
    <div class="close-modal" @click="hide">
      <i class="fas fa-times" />
    </div>

    <Uploader
      v-if="isLoading"
    />

    <div
      class="tab-content"
      v-if="selectedTab === 'upload'"
      style="overflow: hidden;"
      :class="{'tab-upload': selectedTab === 'upload'}"
    >
      <label
        for="upload-file"
        class="horizontal layout center center-justified"
        :class="{'error': error, 'uploaded': uploadDone}"
      >
        <div v-if="!uploadDone">
          <div v-if="!isLoading">
            <i class="far fa-image fa-4x" />
            <h3
              class="dropzone-custom-title"
              v-html="$i18nPresenter.t('pasteDropBrowser')"
            >
            </h3>
            <div class="subtitle">{{ $i18nPresenter.t('Up to 15 MB.') }}</div>
            <div class="p-subtitle">.png .jpg .gif .svg</div>

            <div class="error-text text-danger" v-if="error">{{ error }}</div>
          </div>
        </div>
        <div class="image-upload-done" v-if="uploadDone">
          <img :src="$store.state.slideActive.image.large" alt>
        </div>
        <b-form-file
          v-model="file"
          :state="Boolean(file)"
          choose-label="Attachment2"
          accept="image/*"
          id="upload-file"
          v-if="!isLoading"
          drop-placeholder="drop image"
          :file-name-formatter="onFileSelect"
        />
      </label>
    </div>
    <div
      class="tab-content"
      v-show="selectedTab === 'unsplash'"
      id="infinite-list"
    >
      <div class="input-search">
        <b-form-input
          v-model="text"
          :placeholder="$i18nPresenter.t('Search photos from Unsplash..')"
          @input="searchPhotoUnsplash(1,false)"
          ref="unsplash-search-input"
        />
      </div>
      <div class="list-image">
        <p v-if="noResultText" class="list-image--no-result">
          {{ noResultText }}
        </p>
        <div class="pt-0 item-image" v-for="(item,index) in splashPhotos" :key="index">
          <div
            class="img-bg"
            :style="{ 'background-image': 'url(' + item.urls.small + ')' }"
            @click.self="selectImage(getUnsplashImageUrl(item), 'unsplash', item)"
          >
            <div class="img-info">
              Photo by <a class="img-info__link" :href="'https://unsplash.com/' + item.user.username + '?utm_source=ahaslides&utm_medium=referral'" target="_blank">{{ item.user.name }}</a> on <a class="img-info__unsplash-link" href="https://unsplash.com/?utm_source=ahaslides&utm_medium=referral" target="_blank">Unsplash</a>
            </div>
          </div>
        </div>
      </div>
      <Loading
        style="margin: 0 auto; width: 100%;"
        :class="{'hide-loading-image': !isLoadingImg}"
      />
    </div>
    <div
      class="tab-content"
      v-show="selectedTab === 'giphy'"
      id="infinite-giphy-list"
    >
      <div class="input-search">
        <b-form-input
          v-model="text"
          :placeholder="$i18nPresenter.t('Search GIFs from GIPHY..')"
          @input="searchPhotoGiphy(30,0,false)"
          ref="giphy-search-input"
        />
      </div>
      <div class="list-image">
        <p v-if="noResultText" class="list-image--no-result">
          {{ noResultText }}
        </p>
        <div class="pt-0 item-image" v-for="(item,index) in giphyPhotos" :key="index">
          <div
            class="img-bg"
            :style="{ 'background-image': 'url(' + item.images.preview_gif.url + ')' }"
            @click="selectImage(getGiphyImageUrl(item), 'giphy', item)"
            ref="giphyImg"
          >
            <div class="img-info">
              <img
                alt="PoweredbyGiphy"
                src="../../assets/images/Poweredby_100px-Black_VertLogo.png"
              >
            </div>
          </div>
        </div>
      </div>
      <Loading
        style="margin: 0 auto; width: 100%;"
        :class="{'hide-loading-image': !isLoadingImg}"
      />
      <ModalUploadImageError
        v-if="hasErrorOnUploadingGiphy"
        @handleOkButtonClick="handleOKButtonOnModalUploadImageError()"
      />
    </div>
  </b-modal>
</template>

<script>
import debounce from 'debounce'
import { mapState, mapActions } from 'vuex'
import constants from '@/constant'
import Config from '@/config'
import Socket from '@/socket'
import Loading from '@/components/Loading'
import Utils from '@/util'
import { dataURLToBlob } from '@/utils/indexedDB/image.cache.utils'

// UI
import Uploader from '@/components/UI/loaders/Uploader'
import ModalUploadImageError from '@/components/UI/modals/UploadImageError.vue'
import PasteImageMixin from '@/mixins/presentation/PasteImageMixin'

export default {
  components: {
    Loading,
    Uploader,
    ModalUploadImageError,
  },
  mixins: [PasteImageMixin],
  props: {
    type: {
      type: String,
      default: null,
    },
    expires: { // expiration of signed url by CloudFront
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      tabs: ['upload', 'unsplash', 'giphy'],
      text: '',
      giphyPhotos: [],
      splashPhotos: [],
      file: null,
      error: '',
      isLoading: false,
      uploadDone: false,
      isLoadingImg: false,
      previousSearchText: '',
      noResultText: '',
      noMoreGiphyResult: false,
      noMoreUnsplashResult: false,
      hasErrorOnUploadingGiphy: false,
    }
  },
  computed: {
    ...mapState({
      selectedTab: (state) => state.selections.selectedTab,
    }),
  },
  mounted() {
    this.show()
    this.handleTabChange(this.selectedTab) // default tab: 'upload'
    setTimeout(() => {
      this._setUnslashGiphyInfiniteScroll()
    }, 1000)

    setTimeout(() => {
      // Prevent deselect
      document.getElementById('upload-image-modal').addEventListener('mouseup', (e) => {
        e.stopPropagation()
      }, true)
    }, 0)
  },
  methods: {
    ...mapActions(['setSelectedTabAction', 'updateSlideActiveAction']),
    _setUnslashGiphyInfiniteScroll() {
      const listElm = document.querySelector('#infinite-list')
      const listGiphy = document.querySelector('#infinite-giphy-list')
      if (listElm) {
        listElm.addEventListener('scroll', () => {
          if (this.noMoreUnsplashResult) return
          if (listElm.scrollTop + listElm.clientHeight >= listElm.scrollHeight - 50) {
            this.unsplashInfinite()
          }
        })
      }

      if (listGiphy) {
        listGiphy.addEventListener('scroll', () => {
          if (this.noMoreGiphyResult) return
          if (listGiphy.scrollTop + listGiphy.clientHeight >= listGiphy.scrollHeight - 50) {
            this.giphyInfinite()
          }
        })
      }
    },

    onFileSelect(files) {
      this.uploadFile(files)
      this.trackUploadImage('manual_upload')
    },
    async resizeImage(file) {
      return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onload = function (readerEvent) {
          const image = new Image()
          image.onload = function () {
          // Resize the image
            const canvas = document.createElement('canvas')
            const max_size = 1024
            let { width, height } = image

            if (width > height) {
              if (width > max_size) {
                height *= max_size / width
                width = max_size
              }
            } else if (height > max_size) {
              width *= max_size / height
              height = max_size
            }
            canvas.width = width
            canvas.height = height
            canvas.getContext('2d', { alpha: true }).drawImage(image, 0, 0, width, height)
            const dataUrl = canvas.toDataURL(file.type)
            const resizedImage = dataURLToBlob(dataUrl)
            const resizedFile = new File([resizedImage], file.name, {
              type: file.type,
            })
            resolve(resizedFile)
          }
          image.src = readerEvent.target.result
        }
        reader.readAsDataURL(file)
      })
    },
    async uploadFile(files) {
      if (!files || !files.length) return

      let file = files[0]

      if (file && file.size > 15728640) {
        this.error = this.$i18nPresenter.t('Could not upload this file (max size is 15 MB).')
        return
      }

      const errorString = this.$i18nPresenter.t('Could not upload this file (only support image file jpg, svg, gif, png, ...).')
      if (file.type && !file.type.includes('image')) {
        this.error = errorString
        return
      }

      this.error = ''
      this.isLoading = true
      const formData = new FormData()

      if (this.type === 'contentSlideImage' && file.type && !file.type.includes('image/gif')) {
        file = await this.resizeImage(file)
      }

      formData.append('file', file)
      if (this.expires) {
        formData.append('expires', this.expires)
      }
      formData.append('accessCode', this.$store.state.presentation.uniqueAccessCode)
      formData.append('socketId', Socket.getSocketId())
      formData.append('type', this.type)

      if (file.type === 'image/gif') {
        // need to infer the gif image dimension to be insert as a block
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = (e) => {
          const image = new Image()
          image.src = e.target.result
          image.onload = () => {
            file.width = image.width
            file.height = image.height
          }
        }
      }

      try {
        const res = await this.$http.post(Config.api.uploadImage, formData)
        this.$emit('callback', res.body.file.location, 'manual_upload', {
          width: file.width,
          height: file.height,
        })
      } catch (error) {
        if (error.body && error.body.code === 'file_not_supported') {
          this.error = errorString
        } else {
          this.error = this.$i18nPresenter.t('Cannot upload file. Please try again.')
        }

        this.isLoading = false
        throw error
      }
    },
    async selectImage(url, source, image) {
      this.hasErrorOnUploadingGiphy = false
      this.isLoading = true

      const extension = url.split('?')[0].split('.').pop()

      if (extension === 'gif') {
        try {
          const originalImageUrlRes = await this.$http.post(Config.api.uploadLink, {
            image: url,
            expires: this.expires,
          })

          this.$emit('callback', originalImageUrlRes.body.file, source, {
            width: image.images.original.width,
            height: image.images.original.height,
          })
          this.trackUploadImage(source)
        } catch (error) {
          this.hasErrorOnUploadingGiphy = true
          this.isLoading = false
        }
        return
      }

      this.$emit('callback', url, source)
      this.trackUploadImage(source)
    },

    show() {
      this.uploadDone = false
      this.$refs['create-confirm-modal'].show()
    },
    hide() {
      this.$emit('hideModal')
    },
    trackUploadImage(source) {
      Utils.mixpanelTracking(
        constants.eventCategories.PRESENTATION_EDITOR,
        'Upload image',
        this.type,
        source,
      )
    },
    // implement PasteImageMixin@uploadImgFromPaste
    uploadImgFromPaste(file) {
      if (this.selectedTab !== 'upload') return
      this.uploadFile([file])
      this.trackUploadImage('paste')
    },
    handleTabChange(selectedTab) {
      this.setSelectedTabAction(selectedTab)

      if (selectedTab === 'unsplash') {
        this.searchPhotoUnsplash()
        Utils.mixpanelTracking(
          constants.eventCategories.PRESENTATION_EDITOR,
          'Upload image modal > Open Free Photo (Unsplash)',
        )
        return
      }

      if (selectedTab === 'giphy') {
        this.searchPhotoGiphy()
        Utils.mixpanelTracking(
          constants.eventCategories.PRESENTATION_EDITOR,
          'Upload image modal > Open Giphy',
        )
      }

      if (selectedTab === 'myPhoto') {
        Utils.mixpanelTracking(
          constants.eventCategories.PRESENTATION_EDITOR,
          'Upload image modal > Open My photo',
        )
      }

      if (selectedTab === 'upload') {
        Utils.mixpanelTracking(
          constants.eventCategories.PRESENTATION_EDITOR,
          'Upload image modal > Open Upload',
        )
      }
    },
    searchPhotoUnsplash: debounce(function (infinitePage, e) {
      if (this.previousSearchText !== this.text) {
        this.noMoreUnsplashResult = false
      }
      this.noResultText = ''
      let text = this.text || 'team'
      let page = 1
      if (infinitePage) {
        page = infinitePage
      }
      this.isLoadingImg = true
      text = encodeURIComponent(text)
      const payload = {
        text,
        page,
        imagesPerPage: 30,
      }
      this._handleSearchUnsplash(text, page, e, payload)
    }, 800),

    async _handleSearchUnsplash(text, page, e, payload) {
      const listElm = document.querySelector('#infinite-list')
      try {
        const res = await this.$http.post(Config.api.searchUnsplash, payload)
        if (text && res.body.total === 0) {
          this.noResultText = `We couldn't find any images matching "${this.text}" in Unsplash.`
        }
        if (text && page > res.body.total_pages) {
          this.noMoreUnsplashResult = true // To stop calling searchUnplash API when scroll to bottom
          this.isLoadingImg = false
          return
        }
        if (!e) {
          this.splashPhotos = res.body.results
        } else {
          this.splashPhotos.forEach((splash) => {
            res.body.results = res.body.results.filter((result) => {
              return result.id !== splash.id
            })
          })
          this.splashPhotos.push(...res.body.results)
        }
        if (page === 1 && listElm) {
          listElm.scrollTop = 0
        }
        this.previousSearchText = this.text
        this.isLoadingImg = false
        Utils.mixpanelTracking(
          constants.eventCategories.PRESENTATION_EDITOR,
          'Unsplash > Search',
        )
        setTimeout(() => {
          if (!this.$refs['unsplash-search-input']) return
          this.$refs['unsplash-search-input'].focus()
        })
      } catch (error) {
        Utils.showToastError(this.$i18nPresenter.t('Search images failed. Please try again.'))
      }
    },

    unsplashInfinite() {
      this.noMoreGiphyResult = false
      const totalPagesNow = Math.round(this.splashPhotos.length / 30)
      const infinitePage = totalPagesNow + 1
      this.searchPhotoUnsplash(infinitePage, 'nextPage')
    },

    searchPhotoGiphy: debounce(function (currentInfLimit, infOffset, e) {
      if (this.previousSearchText !== this.text) {
        this.noMoreGiphyResult = false
      }
      this.noResultText = ''
      this.isLoadingImg = true
      let currentLimit
      let offset
      let text = this.text || 'team'
      if (!e) {
        currentLimit = 30
        offset = 0
      } else {
        currentLimit = currentInfLimit
        offset = infOffset
      }
      text = encodeURIComponent(text)
      const payload = {
        text,
        currentLimit,
        offset,
      }
      this._handleSearchGiphy(currentInfLimit, infOffset, text, e, payload)
    }, 800),

    async _handleSearchGiphy(currentInfLimit, infOffset, text, e, payload) {
      const listGiphy = document.querySelector('#infinite-giphy-list')
      try {
        const result = await this.$http.post(Config.api.searchGiphy, payload)
        if (text && !result.body.length) {
          if (infOffset > 0) {
            this.noMoreGiphyResult = true // To stop calling searchGiphy API when scroll to bottom
            this.isLoadingImg = false
            return
          }
          this.noResultText = `We couldn't find any images matching "${this.text}" in Giphy.`
        }
        if (!e) {
          this.giphyPhotos = result.body
        } else {
          this.giphyPhotos.forEach((giphy) => {
            result.body = result.body.filter((photo) => {
              return photo.id !== giphy.id
            })
          })
          this.giphyPhotos.push(...result.body)
        }
        if (currentInfLimit === 30 && infOffset === 0) {
          listGiphy.scrollTop = 0
        }
        this.isLoadingImg = false
        this.previousSearchText = this.text
        Utils.mixpanelTracking(
          constants.eventCategories.PRESENTATION_EDITOR,
          'Giphy > Search',
        )
        setTimeout(() => {
          if (!this.$refs['giphy-search-input']) return
          this.$refs['giphy-search-input'].focus()
        })
      } catch (error) {
        this.isLoadingImg = false
        Utils.showToastError(`${this.$i18nPresenter.t('Search images failed')}${error.body ? `: ${error.body}.` : '.'} ${this.$i18nPresenter.t('Please try again.')}`)
      }
    },

    getUnsplashImageUrl(unsplashPhotoObj) {
      // AHA-8204 - Xử lí load ảnh Unsplash bị chậm.
      // Nguyên nhân: do dùng ảnh RAW, nó nặng, nên thư viện Cropper nó load ảnh chậm.
      // Giải pháp: dùng ảnh optimized, cho phép width tối đa là 1920px
      if (this.type === 'contentSlideImage') {
        return unsplashPhotoObj.urls.regular
      }
      if (unsplashPhotoObj.width > 1920) {
        return `${unsplashPhotoObj.urls.full}&w=1920`
      }
      return unsplashPhotoObj.urls.full
    },

    getGiphyImageUrl(item) {
      return item.images.original.url
    },

    giphyInfinite() {
      this.noMoreUnsplashResult = false
      let currentInfLimit = this.giphyPhotos.length
      const infOffset = currentInfLimit
      currentInfLimit += 30
      this.searchPhotoGiphy(currentInfLimit, infOffset, 'nextPage')
    },
    _updateShowQuestionImage() {
      const { slideActive } = this.$store.state
      if (slideActive.type !== 'multipleChoice' && slideActive.type !== 'imageChoice' && slideActive.type !== 'pickAnswer') {
        slideActive.showQuestionImage = true
        return
      }
      const payload = {
        showQuestionImage: !slideActive.multipleCountAnswer,
      }
      this._setSlideActive(payload)
      this._updateSlide(payload)
    },
    _setSlideActive(payload) {
      this.updateSlideActiveAction({
        showQuestionImage: payload.showQuestionImage,
      })
    },
    async _updateSlide(payload) {
      await this.$http.put(Config.api.updateSlide + this.$store.state.slideActive.id, payload)
        .then(() => {})
    },
    handleOKButtonOnModalUploadImageError() {
      this.hasErrorOnUploadingGiphy = false
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";
.list-image {
  position: relative;
  overflow: hidden;
  margin-top: 21px;
  .list-image--no-result {
    text-align: center;
    font-size: 1em;
  }
}
.uploading-text {
  font-size: 20px;
  font-weight: bold;
}
.hide-loading-image {
  opacity: 0;
}

.input-search {
  background-color: #ffffff;
  z-index: 1;
  & ::v-deep {
    .spinner {
      position: absolute;
      text-align: center;
      top: 50%;
      right: 5px;
      transform: translateY(-50%);
    }
  }
}
.dropzone-custom-title {
  b {
    color: $primary-color;
  }
}
.fa-image {
  margin-bottom: 24px;
  margin-top: 8px;
}
.p-subtitle {
  color: #b9b9b9;
}
.image-upload-done {
  text-align: center;
  img {
    max-height: 400px;
  }
}

.img-info {
  position: absolute;
  bottom: 0;
  background: #0000005e;
  color: #ffffff;
  font-size: 13px;
  padding: 5px;
  text-align: center;
  display: none;
  width: 100%;
  .img-info__link, .img-info__unsplash-link {
    color: #ffffff !important;
  }
  img {
    width: 50%;
  }
}
.item-image {
  width: 25%;
  float: left;
  padding: 5px;

  .img-bg {
    background-position: center center;
    background-repeat: no-repeat;
    background-size: cover;
    height: 160px;
    position: relative;

    .img-info__link {
      text-decoration: underline !important;
    }

    &:hover {
      cursor: pointer;
      .img-info {
        display: block;
      }
    }
  }
}
.subtitle {
  margin-bottom: 6px;
}
</style>
